/* prettier-ignore-file */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { useAuthStore, useUsersStore } from "../stores";
import { getSetting } from "../services/settingsCache";
import PinBoxes from "./PinBoxes";
import {
  ADMIN_PIN_LENGTH,
  DEFAULT_PIN_LENGTH,
  PIN_LENGTH_SETTING_DOC,
  PIN_LENGTH_SETTING_FIELD,
  clampPinLength,
  pinLengthForUser,
  normalizePin,
} from "../utils/pinConfig";
import { colors, radius, spacing, elevation } from "../theme";

const GREETING_MS = 3000;
const ERROR_MS = 2500;

/**
 * The Switch User surface: a translucent scrim over whatever page is beneath,
 * with a PIN pad and nothing to press. The PIN alone identifies the user — that
 * is what makes the "Hello <name>!" greeting possible, and why there is no name
 * picker to tap first.
 *
 * Mounted unconditionally in App.js; renders null when idle. It has to outlive
 * the `switchingUser` flag because the greeting toast keeps showing for three
 * seconds after the pad has closed.
 *
 * Titled "Sign in User" when Login raised it right after the email sign-in
 * (switchUserMode "login": nobody is signed in yet, so there is no one to
 * switch from) and "Switch User" when the header menu did.
 */
const SwitchUserOverlay = () => {
  const switchingUser = useAuthStore((state) => state.switchingUser);
  const switchUserMode = useAuthStore((state) => state.switchUserMode);
  const endSwitchUser = useAuthStore((state) => state.endSwitchUser);
  const setSelectedUser = useAuthStore((state) => state.setSelectedUser);
  const users = useUsersStore((state) => state.users);

  const [storePinLength, setStorePinLength] = useState(DEFAULT_PIN_LENGTH);
  const [digits, setDigits] = useState([]);
  const [adminMode, setAdminMode] = useState(false);
  const [errorToast, setErrorToast] = useState("");
  const [greeting, setGreeting] = useState("");
  const greetingTimer = useRef(null);
  const errorTimer = useRef(null);

  // Admins are pinned to 4 digits while everyone else follows the store setting,
  // so two PIN widths can be live at once. Rather than guess, the pad shows the
  // regular width and offers a toggle to the admin one. When the store setting is
  // already 4 the two coincide, there is nothing to flip to, and the toggle is
  // not rendered.
  const regularLength = clampPinLength(storePinLength);
  const showAdminToggle = regularLength !== ADMIN_PIN_LENGTH;
  const boxCount = adminMode ? ADMIN_PIN_LENGTH : regularLength;

  useEffect(() => () => {
    clearTimeout(greetingTimer.current);
    clearTimeout(errorTimer.current);
  }, []);

  useEffect(() => {
    if (!switchingUser) return undefined;
    let cancelled = false;
    (async () => {
      const value = await getSetting(
        PIN_LENGTH_SETTING_DOC,
        PIN_LENGTH_SETTING_FIELD,
        DEFAULT_PIN_LENGTH
      );
      if (!cancelled) setStorePinLength(clampPinLength(value));
    })();
    return () => {
      cancelled = true;
    };
  }, [switchingUser]);

  // Opening the pad, or flipping the width, starts from an empty row.
  useEffect(() => {
    if (switchingUser) {
      setDigits(Array(boxCount).fill(""));
      setErrorToast("");
    }
  }, [switchingUser, boxCount]);

  useEffect(() => {
    if (!switchingUser) setAdminMode(false);
  }, [switchingUser]);

  const showError = useCallback((message) => {
    setErrorToast(message);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setErrorToast(""), ERROR_MS);
  }, []);

  /** The entered digits belong to whichever user's PIN they match at that width. */
  const findUserByPin = useCallback(
    (entered) => {
      return Object.values(users || {}).find((user) => {
        const required = pinLengthForUser(user, storePinLength);
        if (required !== entered.length) return false;
        const stored = user.pin ?? user.PIN;
        if (!stored) return false;
        return normalizePin(stored, required) === normalizePin(entered, required);
      });
    },
    [users, storePinLength]
  );

  useEffect(() => {
    if (!switchingUser) return;

    const entered = digits.join("");
    if (entered.length !== boxCount) return;

    const match = findUserByPin(entered);
    if (match) {
      const name = `${match.firstName || ""} ${match.lastName || ""}`.trim();
      setSelectedUser(match);
      endSwitchUser();
      setGreeting(name ? `Hello ${name}!` : "Signed in!");
      clearTimeout(greetingTimer.current);
      greetingTimer.current = setTimeout(() => setGreeting(""), GREETING_MS);
      return;
    }

    // Right after login the pad can be up before the users listener has
    // delivered — a no-match then means "nothing to match against yet", not a
    // wrong PIN.
    if (Object.keys(users || {}).length === 0) {
      showError("Still loading the staff list — re-enter your PIN in a moment");
      setDigits(Array(boxCount).fill(""));
      return;
    }

    showError(
      showAdminToggle && !adminMode
        ? `Incorrect PIN — admins, use the ${ADMIN_PIN_LENGTH}-digit toggle`
        : "Incorrect PIN"
    );
    setDigits(Array(boxCount).fill(""));
  }, [
    switchingUser,
    digits,
    boxCount,
    adminMode,
    showAdminToggle,
    users,
    findUserByPin,
    setSelectedUser,
    endSwitchUser,
    showError,
  ]);

  if (!switchingUser && !greeting) return null;

  return (
    <>
      {switchingUser && (
        <View style={styles.scrim}>
          <View style={styles.card}>
            {/* Flip between the regular and admin PIN widths. Hidden when the
                store setting already equals the admin width — nothing to flip. */}
            {showAdminToggle && (
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={[styles.adminToggle, adminMode && styles.adminToggleActive]}
                onPress={() => setAdminMode((v) => !v)}
                accessibilityLabel={
                  adminMode
                    ? `Switch to ${regularLength}-digit PIN`
                    : `Switch to ${ADMIN_PIN_LENGTH}-digit admin PIN`
                }
              >
                <Text style={styles.adminToggleIcon}>{adminMode ? "👤" : "🔑"}</Text>
              </TouchableOpacity>
            )}

            <Text style={styles.title}>
              {switchUserMode === "login" ? "Sign in User" : "Switch User"}
            </Text>
            <Text style={styles.subtitle}>
              {adminMode
                ? `Admin PIN — ${ADMIN_PIN_LENGTH} digits. Signs you in automatically.`
                : "Enter your PIN. It signs you in automatically — no button to press."}
            </Text>

            <PinBoxes
              length={boxCount}
              digits={digits}
              onChange={setDigits}
              autoFocus={switchingUser}
            />

            {errorToast ? (
              <View style={styles.errorToast}>
                <Text style={styles.errorToastText}>{errorToast}</Text>
              </View>
            ) : null}
          </View>
        </View>
      )}

      {greeting ? (
        <View style={styles.greetingToast} pointerEvents="none">
          <Text style={styles.greetingToastText}>{greeting}</Text>
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  scrim: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
    backdropFilter: "blur(2px)",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.xl,
    zIndex: 3000,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: spacing.xl,
    width: "100%",
    maxWidth: 420,
    alignItems: "center",
    boxShadow: elevation.xl,
    elevation: 20,
  },
  adminToggle: {
    position: "absolute",
    top: spacing.md,
    right: spacing.md,
    width: 36,
    height: 36,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceMuted,
    alignItems: "center",
    justifyContent: "center",
  },
  adminToggleActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primarySoft,
  },
  adminToggleIcon: {
    fontSize: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    color: colors.ink900,
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: 14,
    color: colors.ink500,
    textAlign: "center",
    marginBottom: spacing.xl,
    lineHeight: 20,
  },
  errorToast: {
    marginTop: spacing.lg,
    backgroundColor: colors.dangerSoft,
    borderLeftWidth: 4,
    borderLeftColor: colors.danger,
    borderRadius: radius.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  errorToastText: {
    color: colors.danger,
    fontSize: 14,
    fontWeight: "600",
  },
  // Sits above the scrim's z-index so it is still visible in the frame where the
  // pad is tearing down.
  greetingToast: {
    position: "fixed",
    top: 24,
    left: "50%",
    transform: [{ translateX: "-50%" }],
    backgroundColor: colors.primary,
    borderRadius: radius.pill,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    boxShadow: elevation.lg,
    elevation: 12,
    zIndex: 3100,
  },
  greetingToastText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: "700",
  },
});

export default SwitchUserOverlay;
